import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
} from '@mui/material';

export interface ChooseWellAddressDialogProps {
  open: boolean;
  rows: number;
  columns: number;
  selected: { name: string; row: string; column: string }[];
  onConfirm: (wells: { name: string; row: string; column: string }[]) => void;
  onClose: () => void;
}

function rowLabel(index: number): string {
  return String.fromCharCode(65 + index); // A, B, C...
}

function colLabel(index: number): string {
  return String(index + 1);
}

function wellName(rowIdx: number, colIdx: number): string {
  return `${rowLabel(rowIdx)}${colLabel(colIdx)}`;
}

export function ChooseWellAddressDialog({
  open,
  rows,
  columns,
  selected: initialSelected,
  onConfirm,
  onClose,
}: ChooseWellAddressDialogProps) {
  const [selectedSet, setSelectedSet] = useState<Set<string>>(
    () => new Set(initialSelected.map((w) => w.name)),
  );

  const grid = useMemo(() => {
    const cells: { name: string; row: string; column: string }[][] = [];
    for (let r = 0; r < rows; r++) {
      const rowCells: { name: string; row: string; column: string }[] = [];
      for (let c = 0; c < columns; c++) {
        rowCells.push({
          name: wellName(r, c),
          row: rowLabel(r),
          column: colLabel(c),
        });
      }
      cells.push(rowCells);
    }
    return cells;
  }, [rows, columns]);

  const handleClick = (name: string, event: React.MouseEvent) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (event.ctrlKey || event.metaKey) {
        if (next.has(name)) next.delete(name);
        else next.add(name);
      } else {
        if (next.size === 1 && next.has(name)) {
          next.clear();
        } else {
          next.clear();
          next.add(name);
        }
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const wells = grid.flat().filter((w) => selectedSet.has(w.name));
    onConfirm(wells);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Choose Well Address</DialogTitle>
      <DialogContent>
        {rows === 0 || columns === 0 ? (
          <Typography color="text.secondary">
            Set rows and columns first.
          </Typography>
        ) : (
          <Grid
            container
            sx={{ gap: '2px', mt: 1 }}
            data-testid="well-grid"
          >
            {grid.map((row, rIdx) => (
              <Grid container key={rIdx} sx={{ gap: '2px' }}>
                {row.map((cell) => (
                  <Button
                    key={cell.name}
                    variant={selectedSet.has(cell.name) ? 'contained' : 'outlined'}
                    size="small"
                    onClick={(e) => handleClick(cell.name, e)}
                    sx={{
                      minWidth: 40,
                      minHeight: 32,
                      fontSize: '0.75rem',
                      padding: '2px 4px',
                    }}
                    data-testid={`well-${cell.name}`}
                  >
                    {cell.name}
                  </Button>
                ))}
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
